// Note:
// This file contains a new pattern for
// setting up queries utilizing queryOptions from @tanstack/react-query
// this allows the the easy generation of query keys and query functions
// That are tied together under a single function.
// It also gives greater control over the utilization as the options are passed in during
// use and not all ahead of time, i.e enabled, select, etc.
import { queryOptions } from '@tanstack/react-query';

import { StoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import type {
  ConsumerPostCheckoutData,
  ConsumerPostCheckoutReceipt,
  ConsumerPostCheckoutRedeemableTicketsResponse,
} from './post-checkout.types';

interface ConsumerPostCheckoutReceiptResponse {
  checkout: ConsumerPostCheckoutReceipt;
}
type ConsumerPostCheckoutDataResponse = {
  checkout: ConsumerPostCheckoutData;
};

/**
 * Show the data for a given post-checkout id.
 *
 * If a cart id is provided, it will be used to fetch the data instead of the post-checkout id
 * This will return a Checkout object with the data. The checkout objected returned
 * is only available during render time, it is not saved to the database and contains no uuid.
 *
 * if no post-checkout id or cart id is provided, the query will not be enabled..
 */
const showConsumerPostCheckoutQuery = ({
  postCheckoutId,
  cartId,
}: {
  postCheckoutId?: string;
  cartId?: string;
}) => {
  /** Parameter derived from the post checkout id or cart id */
  const postCheckoutIdParam = postCheckoutId ?? cartId;
  const getConsumerPostCheckoutData = async () => {
    const checkoutUrlParam = cartId ? 'new' : postCheckoutId;
    const { data } = await StoreApi.get<ConsumerPostCheckoutDataResponse>(
      `/consumer/post-checkout/checkouts/${checkoutUrlParam}`,
      {
        params: {
          cartId,
        },
      }
    );
    return data.checkout;
  };

  const key = postCheckoutIdParam
    ? storeKeys.id('user').consumer.postCheckout.id(postCheckoutIdParam).detail
    : [];

  return queryOptions({
    queryKey: key,
    queryFn: getConsumerPostCheckoutData,
    enabled: !!postCheckoutIdParam,
  });
};

/**
 * Show the receipt for a given post-checkout id.
 *
 * If a cart id is provided, it will be used to fetch the receipt instead of the post-checkout id
 * This will return a Checkout object with the receipt data. The checkout objected returned
 * is only available during render time, it is not saved to the database and contains no uuid.
 *
 * if no post-checkout id or cart id is provided, the query will not be enabled.
 * 
 *  * @note Enabled is set as a default to be true if the post-checkout id or cart id is provided but this can be overridden by the enabled parameter on the useQuery call.

 */
const showConsumerPostCheckoutReceiptQuery = ({
  postCheckoutId,
  cartId,
}: {
  postCheckoutId?: string;
  cartId?: string;
}) => {
  const postCheckoutIdParam = postCheckoutId ?? cartId;
  const getConsumerPostCheckoutCartViews = async () => {
    const checkoutUrlParam = cartId ? 'new' : postCheckoutId;
    const { data } = await StoreApi.get<ConsumerPostCheckoutReceiptResponse>(
      `/consumer/post-checkout/checkouts/${checkoutUrlParam}/receipt`,
      {
        params: {
          cartId,
        },
      }
    );

    return data.checkout;
  };

  const key = postCheckoutIdParam
    ? storeKeys.id('user').consumer.postCheckout.id(postCheckoutIdParam).receipt
    : [];
  return queryOptions({
    queryKey: key,
    queryFn: getConsumerPostCheckoutCartViews,
    enabled: !!postCheckoutIdParam,
  });
};

/**
 * List the redeemable tickets for a given post-checkout id.
 *
 * If a cart id is provided, it will be used to fetch the tickets instead of the post-checkout id
 * This will return a list of redeemable tickets. The list of tickets returned
 * is only available during render time, it is not saved to the database and contains no uuid.
 *
 * @note Enabled is set as a default to be true if the post-checkout id or cart id is provided but this can be overridden by the enabled parameter on the useQuery call.
 */
const listConsumerPostCheckoutRedeemableTicketsQuery = ({
  postCheckoutId,
  email,
  cartId,
}: {
  postCheckoutId?: string;
  email?: string;
  /** When a cart id is provided, it will be used to fetch the tickets instead of the post-checkout id */
  cartId?: string;
}) => {
  const postCheckoutIdParam = postCheckoutId ?? cartId;
  const getConsumerPostCheckoutRedeemableTickets = async () => {
    const checkoutUrlParam = cartId ? 'new' : postCheckoutId;
    const { data } = await StoreApi.get<ConsumerPostCheckoutRedeemableTicketsResponse>(
      `/consumer/post-checkout/checkouts/${checkoutUrlParam}/tickets`,
      {
        params: {
          email,
          cartId,
        },
      }
    );
    return data.tickets;
  };
  const key = postCheckoutIdParam
    ? [
        ...storeKeys.id('user').consumer.postCheckout.id(postCheckoutIdParam).redeemableTickets,
        { email },
      ]
    : [];
  return queryOptions({
    queryKey: key,
    queryFn: getConsumerPostCheckoutRedeemableTickets,
    enabled: !!postCheckoutIdParam,
  });
};

export {
  listConsumerPostCheckoutRedeemableTicketsQuery,
  showConsumerPostCheckoutQuery,
  showConsumerPostCheckoutReceiptQuery,
};
